<template>
  <div class="min-h-screen bg-white">
    <!-- HERO INSTITUCIONAL -->
    <section class="relative bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white overflow-hidden">
      <div class="absolute inset-0 opacity-10">
        <div class="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
        <div class="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500 rounded-full blur-3xl" />
      </div>
      <div class="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
        <div class="max-w-3xl mx-auto text-center">
          <div class="inline-flex items-center gap-2 bg-blue-600/30 border border-blue-500/30 rounded-full px-4 py-1.5 text-sm font-medium text-blue-300 mb-6">
            <span class="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
            Digital Software Licensing Made Simple
          </div>
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
            GVGmall<br />
            <span class="text-blue-400">Digital Software Licensing</span>
          </h1>
          <p class="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            We provide genuine digital software licenses with instant delivery and professional customer support. Trusted by users worldwide for secure, authentic software solutions.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@gvgmall.co"
              class="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-4 rounded-xl text-lg shadow-lg transition"
            >
              Contact Support
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- TRUST BADGES -->
    <section class="bg-slate-50 border-b border-slate-100">
      <div class="max-w-7xl mx-auto px-6 py-10">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div v-for="badge in trustBadges" :key="badge.title" class="flex items-start gap-3">
            <div class="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-xl">
              {{ badge.icon }}
            </div>
            <div>
              <div class="font-bold text-slate-900 text-sm">{{ badge.title }}</div>
              <div class="text-xs text-slate-500 mt-0.5">{{ badge.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ABOUT GVGmall -->
    <section class="max-w-7xl mx-auto px-6 py-16">
      <div class="max-w-3xl mx-auto text-center mb-12">
        <h2 class="text-2xl md:text-3xl font-extrabold text-slate-900">About GVGmall</h2>
        <p class="text-slate-500 mt-3">
          GVGmall is a digital software licensing platform dedicated to providing genuine software licenses with a focus on transparency, security, and customer satisfaction.
        </p>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div class="space-y-4">
          <p class="text-slate-600 leading-relaxed">
            Founded with the mission to make software licensing accessible and trustworthy, GVGmall serves customers worldwide with a commitment to authenticity and reliability.
          </p>
          <p class="text-slate-600 leading-relaxed">
            We partner with authorized distributors to ensure every license we provide is 100% genuine and fully supported by official channels.
          </p>
        </div>
        <div class="bg-slate-50 rounded-2xl p-8 border border-slate-100">
          <h3 class="font-bold text-slate-900 mb-4">Our Commitment</h3>
          <ul class="space-y-3">
            <li v-for="item in commitments" :key="item" class="flex items-center gap-3 text-slate-600">
              <span class="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm">✓</span>
              {{ item }}
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- WHAT WE PROVIDE -->
    <section class="bg-slate-50 py-16">
      <div class="max-w-7xl mx-auto px-6">
        <div class="text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-extrabold text-slate-900">What We Provide</h2>
          <p class="text-slate-500 mt-2">Comprehensive digital software licensing solutions</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="service in services"
            :key="service.title"
            class="bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-200 hover:shadow-sm transition"
          >
            <div class="text-3xl mb-3">{{ service.icon }}</div>
            <h3 class="font-bold text-slate-900 mb-2">{{ service.title }}</h3>
            <p class="text-sm text-slate-500">{{ service.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- DIGITAL DELIVERY -->
    <section class="max-w-7xl mx-auto px-6 py-16">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 class="text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">Digital Delivery</h2>
          <p class="text-slate-600 mb-6 leading-relaxed">
            All our software licenses are delivered digitally via email, ensuring you receive your product key quickly and securely. No physical shipping required — your license is just minutes away from activation.
          </p>
          <div class="space-y-4">
            <div v-for="feature in deliveryFeatures" :key="feature.title" class="flex items-start gap-3">
              <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 flex-shrink-0">
                {{ feature.icon }}
              </div>
              <div>
                <h4 class="font-semibold text-slate-900 text-sm">{{ feature.title }}</h4>
                <p class="text-sm text-slate-500">{{ feature.desc }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
          <div class="text-center">
            <div class="text-6xl mb-4">📧</div>
            <h3 class="font-bold text-slate-900 mb-2">Instant Email Delivery</h3>
            <p class="text-slate-600 text-sm">
              License keys are sent to your registered email address immediately after order confirmation, available 24/7.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CUSTOMER SUPPORT -->
    <section class="bg-slate-50 py-16">
      <div class="max-w-7xl mx-auto px-6">
        <div class="max-w-3xl mx-auto text-center mb-12">
          <h2 class="text-2xl md:text-3xl font-extrabold text-slate-900">Customer Support</h2>
          <p class="text-slate-500 mt-3">
            Our dedicated support team is here to help you with any questions about your license or activation process.
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="bg-white border border-slate-200 rounded-2xl p-6 text-center">
            <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-2xl mx-auto mb-4">
              📧
            </div>
            <h3 class="font-bold text-slate-900 mb-2">Email Support</h3>
            <p class="text-sm text-slate-500 mb-4">Reach us anytime at</p>
            <a href="mailto:support@gvgmall.co" class="text-blue-600 font-semibold hover:text-blue-700">
              support@gvgmall.co
            </a>
          </div>
          <div class="bg-white border border-slate-200 rounded-2xl p-6 text-center">
            <div class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-2xl mx-auto mb-4">
              ⚡
            </div>
            <h3 class="font-bold text-slate-900 mb-2">Quick Response</h3>
            <p class="text-sm text-slate-500">
              We strive to respond to all inquiries within 24 hours during business days.
            </p>
          </div>
          <div class="bg-white border border-slate-200 rounded-2xl p-6 text-center">
            <div class="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-2xl mx-auto mb-4">
              🛡️
            </div>
            <h3 class="font-bold text-slate-900 mb-2">Technical Assistance</h3>
            <p class="text-sm text-slate-500">
              Get help with license activation and software installation guidance.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- SECURE ONLINE EXPERIENCE -->
    <section class="max-w-7xl mx-auto px-6 py-16">
      <div class="bg-gradient-to-r from-slate-900 to-blue-950 rounded-2xl p-8 md:p-12 text-white">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 class="text-2xl md:text-3xl font-extrabold mb-4">Secure Online Experience</h2>
            <p class="text-slate-300 mb-6 leading-relaxed">
              Your security is our priority. We implement industry-standard security measures to protect your information and ensure safe transactions.
            </p>
            <ul class="space-y-3">
              <li v-for="security in securityFeatures" :key="security" class="flex items-center gap-3 text-slate-300">
                <span class="w-5 h-5 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm">✓</span>
                {{ security }}
              </li>
            </ul>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div class="bg-white/10 rounded-xl p-4 text-center">
              <div class="text-3xl mb-2">🔒</div>
              <div class="font-semibold text-sm">SSL Encryption</div>
            </div>
            <div class="bg-white/10 rounded-xl p-4 text-center">
              <div class="text-3xl mb-2">🛡️</div>
              <div class="font-semibold text-sm">Data Protection</div>
            </div>
            <div class="bg-white/10 rounded-xl p-4 text-center">
              <div class="text-3xl mb-2">✅</div>
              <div class="font-semibold text-sm">Verified Licenses</div>
            </div>
            <div class="bg-white/10 rounded-xl p-4 text-center">
              <div class="text-3xl mb-2">🌐</div>
              <div class="font-semibold text-sm">Global Standards</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONTACT INFORMATION -->
    <section class="bg-slate-50 py-16">
      <div class="max-w-4xl mx-auto px-6 text-center">
        <h2 class="text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">Contact Information</h2>
        <p class="text-slate-500 mb-8">
          Have questions? We're here to help. Reach out to us through the following channels:
        </p>
        <div class="bg-white border border-slate-200 rounded-2xl p-8 max-w-lg mx-auto">
          <div class="flex items-center justify-center gap-3 mb-4">
            <div class="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-xl">
              📧
            </div>
            <div class="text-left">
              <div class="text-sm text-slate-500">Support Email</div>
              <a href="mailto:support@gvgmall.co" class="text-blue-600 font-semibold hover:text-blue-700">
                support@gvgmall.co
              </a>
            </div>
          </div>
          <p class="text-sm text-slate-500">
            For general inquiries, support requests, and business partnerships.
          </p>
        </div>
      </div>
    </section>

    <!-- FOOTER INSTITUCIONAL -->
    <footer class="bg-slate-900 text-slate-300 py-12">
      <div class="max-w-7xl mx-auto px-6">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div>
            <h4 class="font-bold text-white mb-4">GVGmall</h4>
            <p class="text-sm text-slate-400">
              Digital software licensing solutions with secure delivery and professional support.
            </p>
          </div>
          <div>
            <h4 class="font-bold text-white mb-4">Legal</h4>
            <ul class="space-y-2 text-sm">
              <li><NuxtLink to="/legal/privacy-policy" class="hover:text-white transition">Privacy Policy</NuxtLink></li>
              <li><NuxtLink to="/legal/terms-of-service" class="hover:text-white transition">Terms of Service</NuxtLink></li>
              <li><NuxtLink to="/legal/refund-policy" class="hover:text-white transition">Refund Policy</NuxtLink></li>
            </ul>
          </div>
          <div>
            <h4 class="font-bold text-white mb-4">Support</h4>
            <ul class="space-y-2 text-sm">
              <li><a href="mailto:support@gvgmall.co" class="hover:text-white transition">Contact Support</a></li>
              <li><NuxtLink to="/legal/support" class="hover:text-white transition">Help Center</NuxtLink></li>
            </ul>
          </div>
          <div>
            <h4 class="font-bold text-white mb-4">Contact</h4>
            <ul class="space-y-2 text-sm">
              <li>
                <a href="mailto:support@gvgmall.co" class="hover:text-white transition">
                  support@gvgmall.co
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div class="border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
          <p>&copy; {{ new Date().getFullYear() }} GVGmall. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
// SEO Meta Tags
useSeoMeta({
  title: 'GVGmall | Digital Software Licensing',
  description: 'GVGmall provides digital software licensing solutions with secure online delivery and customer support. Genuine licenses, instant delivery, professional support.',
  ogTitle: 'GVGmall | Digital Software Licensing',
  ogDescription: 'GVGmall provides digital software licensing solutions with secure online delivery and customer support.',
  ogType: 'website',
  ogUrl: 'https://www.gvgmall.co',
  robots: 'index,follow'
})

useHead({
  link: [
    { rel: 'canonical', href: 'https://www.gvgmall.co' }
  ]
})

const trustBadges = [
  { icon: '⚡', title: 'Instant Delivery', desc: 'License key sent to your email in minutes.' },
  { icon: '🔒', title: 'Secure Payment', desc: 'Powered by Stripe — no card data stored.' },
  { icon: '✅', title: 'Genuine Licenses', desc: '100% original, direct from official sources.' },
  { icon: '🌍', title: 'Global Support', desc: 'Customer support available worldwide.' },
]

const commitments = [
  '100% Genuine Software Licenses',
  'Instant Digital Delivery',
  'Secure Payment Processing',
  'Professional Customer Support',
  'Transparent Business Practices',
  'Data Privacy Protection'
]

const services = [
  { icon: '🪟', title: 'Windows Licenses', desc: 'Genuine Windows operating system licenses for personal and business use.' },
  { icon: '📊', title: 'Office Suites', desc: 'Microsoft Office and productivity software licenses.' },
  { icon: '🛡️', title: 'Security Software', desc: 'Antivirus and security solution licenses to protect your devices.' },
  { icon: '🎨', title: 'Creative Tools', desc: 'Design and creative software licenses for professionals.' },
  { icon: '🖥️', title: 'Server Solutions', desc: 'Windows Server and enterprise software licensing.' },
  { icon: '🔧', title: 'Technical Support', desc: 'Professional assistance with installation and activation.' },
]

const deliveryFeatures = [
  { icon: '⚡', title: 'Immediate Delivery', desc: 'License keys sent within minutes of purchase confirmation.' },
  { icon: '📧', title: 'Email Notification', desc: 'Clear instructions and product key delivered to your inbox.' },
  { icon: '🔄', title: 'Order Tracking', desc: 'Access your order history and license information anytime.' },
  { icon: '💾', title: 'Secure Storage', desc: 'Your license information is safely stored and accessible.' },
]

const securityFeatures = [
  'SSL encryption for all data transmission',
  'Secure payment processing via Stripe',
  'No storage of sensitive payment information',
  'Privacy-compliant data handling practices',
  'Regular security audits and updates'
]
</script>
